import { apiService } from './api';

export type InfluenceCategory = {
  id: string;
  key: string;
  label: string;
  color: string;
  description?: string;
};

export type InfluenceScore = {
  id: string;
  entityType: 'stakeholder' | 'organization';
  entityId: string;
  categoryId: string;
  score: number;
  confidence?: number;
  source: string;
  category?: InfluenceCategory;
};

// Categories API
export async function getInfluenceCategories() {
  const res = await apiService.get<InfluenceCategory[]>(`/api/taxonomies/influence-categories`);
  return res.data;
}

export async function getInfluenceScores(entityType: 'stakeholder' | 'organization', entityId: string) {
  const res = await apiService.get<InfluenceScore[]>(`/api/taxonomies/influence-scores/${entityType}/${entityId}`);
  return res.data;
}

// Get influence data for a specific entity with categories
export async function getEntityInfluenceData(entityType: 'stakeholder' | 'organization', entityId: string) {
  const [scores, categories] = await Promise.all([
    getInfluenceScores(entityType, entityId),
    getInfluenceCategories()
  ]);
  return { scores, categories };
}

export async function upsertInfluenceScores(entityType: 'stakeholder' | 'organization', entityId: string, scores: { categoryId: string; score: number; confidence?: number; notes?: string }[]) {
  const res = await apiService.patch(`/api/influence-scores`, { entityType, entityId, scores });
  return res.data as InfluenceScore[];
}

// Get all influence data for visualization
export async function getAllInfluenceData() {
  const res = await apiService.get<{ categories: InfluenceCategory[]; scores: InfluenceScore[] }>(`/api/taxonomies/influence-data`);
  return res.data;
}

// --- Influence Analytics types ---

export type InfluenceCategoryScore = {
  categoryId: string;
  categoryKey: string;
  categoryLabel: string;
  categoryColor: string;
  score: number;
  confidence?: number;
  source: string;
};

export type InfluenceEntity = {
  id: string;
  type: 'stakeholder' | 'organization';
  label: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  category?: string;
  orgType?: string;
  location?: string;
  status?: string;
  bio?: string;
  description?: string;
  website?: string;
  profileImageId?: string;
  logoId?: string;
  parentId?: string;
  scores: InfluenceCategoryScore[];
  totalScore: number;
  avgScore: number;
  maxScore: number;
  avgConfidence: number;
  dominantCategory: {
    id: string;
    key: string;
    label: string;
    color: string;
    score: number;
  } | null;
  scoreCount: number;
};

export type InfluenceConnection = {
  source: string;
  target: string;
  type: 'assignment' | 'relationship' | 'hierarchy' | 'influence_correlation';
  label?: string;
  category?: string;
  categoryId?: string;
  categoryColor?: string;
  weight?: number;
  isCurrent?: boolean;
};

export type InfluenceInsights = {
  topInfluencers: {
    id: string;
    label: string;
    type: string;
    avgScore: number;
    maxScore: number;
    dominantCategory: any;
  }[];
  categoryLeaders: {
    categoryId: string;
    categoryLabel: string;
    categoryColor: string;
    entityId: string;
    entityLabel: string;
    entityType: string;
    score: number;
  }[];
  avgScores: Record<string, number>;
  totalEntities: number;
  totalStakeholders: number;
  totalOrganizations: number;
};

export type InfluenceAnalyticsData = {
  categories: InfluenceCategory[];
  entities: InfluenceEntity[];
  connections: InfluenceConnection[];
  insights: InfluenceInsights;
};

// Optimized single-call analytics endpoint
export async function getInfluenceAnalytics(): Promise<InfluenceAnalyticsData> {
  const res = await apiService.get<InfluenceAnalyticsData>(`/api/taxonomies/influence-analytics`);
  return res.data;
}
